/**
 * La «coincidencia» del asistente arancelario de Comercia (maqueta de Melanie:
 * «92 % · coincide: split · aire acondicionado · pared»).
 *
 * No es una probabilidad que alguien midió. Es cuánto de lo que escribió la
 * persona aparece en el texto de cada partida (su descripción y la de sus
 * padres en el arancel), con más peso si aparece en la descripción propia, y
 * repartido entre las partidas que se muestran para que sumen 100. Las palabras
 * que calzaron se muestran al lado: el porcentaje se puede auditar.
 */

/** Palabras que no dicen qué es el producto. */
const VACIAS = new Set([
  'de',
  'del',
  'la',
  'las',
  'el',
  'los',
  'un',
  'una',
  'unos',
  'unas',
  'y',
  'o',
  'u',
  'e',
  'en',
  'con',
  'sin',
  'para',
  'por',
  'a',
  'al',
  'que',
  'se',
  'su',
  'sus',
  'es',
  'solo',
  'tipo',
  'marca',
  'modelo',
  'como',
  'muy',
  'mas',
  'más',
  'otro',
  'otra',
  'otros',
  'otras',
  'pulgadas',
  'pulgada',
  'cm',
  'mm',
  'kg',
  'w',
  'v',
  'btu',
  'hp',
  'gb',
  'tb',
  'mah',
]);

export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Las palabras de la consulta que importan, sin repetir y en su orden. */
export function terminosDe(consulta: string): string[] {
  const vistos = new Set<string>();
  return normalizar(consulta)
    .split(/[^a-z0-9ñ]+/)
    .filter((t) => t.length >= 3 && !VACIAS.has(t) && !/\d/.test(t))
    .filter((t) => (vistos.has(t) ? false : (vistos.add(t), true)));
}

/**
 * ¿Aparece el término en el texto? Por la raíz: tolera el plural («aires» ↔
 * «aire») y las otras formas de la misma palabra («acondicionado» ↔
 * «acondicionamiento»), que es como el arancel nombra las cosas.
 */
export function aparece(termino: string, texto: string): boolean {
  const sinPlural = termino.length > 4 && termino.endsWith('s') ? termino.slice(0, -1) : termino;
  const raiz = sinPlural.length > 7 ? sinPlural.slice(0, sinPlural.length - 3) : sinPlural;
  return new RegExp(`\\b${raiz}[a-z0-9ñ]*`).test(texto);
}

export interface PartidaParaPuntuar {
  description: string;
  fullContext?: string | null;
}

export interface Puntuada<T> {
  partida: T;
  /** De 0 a 100; las mostradas suman 100. */
  coincidencia: number;
  /** Las palabras de la consulta que aparecen en la partida. */
  coincide: string[];
  /** Las palabras de lo que entendió la IA que aparecen en la partida (cuando lo escrito no calza). */
  coincideIa?: string[];
}

/**
 * Lo que la IA del buscador entendió del producto («Panel fotovoltaico…» y los
 * términos del arancel que propuso). Afina el orden, pero pesa menos que lo que
 * escribió la persona: la IA a veces propone de más («aparatos receptores de
 * televisión» para un monitor).
 */
export interface Pistas {
  concepto?: string | null;
  terminosLegales?: string[];
}

/**
 * Palabras que el arancel dice de otra forma. Una palabra de la consulta cuenta
 * también donde aparece su equivalente.
 *
 * Los monitores y las notebooks el arancel los nombra por la máquina de la
 * partida 84.71 («máquina automática para tratamiento o procesamiento de datos»);
 * a las sillas, «asientos»; a la silla de oficina, «giratorio de altura ajustable».
 */
const EQUIVALENTES: Record<string, string[]> = {
  silla: ['asiento'],
  sillas: ['asiento'],
  sillon: ['asiento'],
  butaca: ['asiento'],
  oficina: ['giratori'],
  solar: ['fotovoltaic'],
  solares: ['fotovoltaic'],
  frio: ['enfriamiento', 'refrigeracion'],
  monitor: ['procesamiento de datos'],
  monitores: ['procesamiento de datos'],
  computadora: ['procesamiento de datos'],
  computador: ['procesamiento de datos'],
  notebook: ['procesamiento de datos', 'portatil'],
  laptop: ['procesamiento de datos', 'portatil'],
  celular: ['telefono'],
  smartphone: ['telefono'],
  televisor: ['television'],
  bateria: ['acumulador'],
  light: ['alumbrado', 'luminaria'],
  luz: ['alumbrado', 'luminaria'],
  iluminacion: ['alumbrado', 'luminaria'],
  lampara: ['alumbrado', 'luminaria'],
  luminaria: ['alumbrado'],
  foco: ['alumbrado', 'luminaria'],
};

/**
 * Búsquedas que conviene sumar porque el arancel usa otra palabra y el buscador
 * no la trae sola: «silla de oficina» traía coches de bebé, y las sillas están en
 * «asientos» del capítulo 94; un panel LED, en las luminarias del mismo capítulo.
 */
const BUSCAR_TAMBIEN: Record<string, BusquedaCorta> = {
  silla: { q: 'asientos', capitulo: '94' },
  sillas: { q: 'asientos', capitulo: '94' },
  sillon: { q: 'asientos', capitulo: '94' },
  solar: { q: 'fotovoltaicas' },
  solares: { q: 'fotovoltaicas' },
  light: { q: 'luminarias', capitulo: '94' },
  lampara: { q: 'luminarias', capitulo: '94' },
  luminaria: { q: 'luminarias', capitulo: '94' },
  foco: { q: 'luminarias', capitulo: '94' },
};

export interface BusquedaCorta {
  q: string;
  capitulo?: string;
}

/**
 * Las búsquedas extra para afinar: una por término del arancel que propuso la IA
 * (sus dos últimas palabras con contenido, que es donde el arancel pone lo
 * específico: «acondicionamiento de aire», «módulos fotovoltaicos») y las de
 * `BUSCAR_TAMBIEN`. Cortas y sin números, para que el buscador las resuelva por
 * texto. Medido en DEV: «módulos fotovoltaicos» trae 8541.43 en 0,8 s, cuando la
 * descripción completa del panel solar traía puertas de aluminio.
 */
export function busquedasCortas(consulta: string, pistas: Pistas, maximo = 5): BusquedaCorta[] {
  const cortas: BusquedaCorta[] = [];
  // Primero las de la consulta: son las que el buscador no encuentra solo.
  for (const t of terminosDe(consulta)) {
    if (BUSCAR_TAMBIEN[t]) cortas.push(BUSCAR_TAMBIEN[t]);
  }
  for (const termino of pistas.terminosLegales ?? []) {
    const palabras = termino.split(/\s+/).filter((p) => {
      const n = normalizar(p).replace(/[^a-z0-9ñ]/g, '');
      return n.length >= 3 && !VACIAS.has(n) && !/\d/.test(n);
    });
    if (palabras.length > 0) cortas.push({ q: palabras.slice(-2).join(' ') });
  }
  const q = normalizar(consulta.trim());
  const vistas = new Set<string>();
  return cortas
    .filter((c) => {
      const clave = `${normalizar(c.q)}|${c.capitulo ?? ''}`;
      if (normalizar(c.q) === q || vistas.has(clave)) return false;
      vistas.add(clave);
      return true;
    })
    .slice(0, maximo);
}

/** Cuánto vale encontrar una palabra según dónde: en la partida, en su padre o más arriba. */
const PESO_PROPIA = 3;
const PESO_PADRE = 2;
const PESO_ARRIBA = 1;

/**
 * Lo que viene después de «excepto» o «sin» niega, no describe: «Bombas de
 * calor, excepto las máquinas para acondicionamiento de aire» no es un aire
 * acondicionado, y «células sin ensamblar en módulos» no es un panel.
 */
const NEGACION = /\b(excepto|salvo|sin|exclu[a-z]*|distint[oa]s? de|que no)\b/;

function sinLoNegado(texto: string): string {
  const m = NEGACION.exec(texto);
  return m ? texto.slice(0, m.index) : texto;
}

/** La descripción propia, la del padre y la de todo lo de arriba, sin acentos. */
function nivelesDe(p: PartidaParaPuntuar): [string, string, string] {
  const propia = normalizar(p.description).trim();
  const camino = (p.fullContext ?? '')
    .split('>')
    .map((s) =>
      normalizar(s)
        .replace(/[:.;]+$/, '')
        .trim()
    )
    .filter(Boolean);
  // El camino suele terminar en la propia descripción: no se cuenta dos veces.
  const ultimo = camino[camino.length - 1];
  if (ultimo && (ultimo === propia || propia.startsWith(ultimo) || ultimo.startsWith(propia)))
    camino.pop();
  const padre = camino.pop() ?? '';
  return [sinLoNegado(propia), sinLoNegado(padre), camino.map(sinLoNegado).join(' ')];
}

/** Las formas en que una palabra puede estar en el arancel: ella misma y sus equivalentes. */
function formasDe(termino: string): Array<(texto: string) => boolean> {
  return [
    (texto) => aparece(termino, texto),
    ...(EQUIVALENTES[termino] ?? []).map((e) => (texto: string) => texto.includes(e)),
  ];
}

/** El mejor nivel donde aparece una forma, y medio punto por cada otro nivel. */
function puntosDeForma(esta: (texto: string) => boolean, niveles: string[]): number {
  const pesos = [PESO_PROPIA, PESO_PADRE, PESO_ARRIBA];
  const donde = niveles.map((t, i) => (t && esta(t) ? pesos[i] : 0)).filter((x) => x > 0);
  return donde.length === 0 ? 0 : Math.max(...donde) + 0.5 * (donde.length - 1);
}

/** Lo que casi nunca es lo que se busca, salvo que se pida con esas palabras. */
const SALVO_QUE_SE_PIDA: Array<{ en: RegExp; pide: RegExp; resta: number; soloPropia?: boolean }> =
  [
    // Una máquina entera no es su repuesto.
    {
      en: /^partes?\b/,
      pide: /\b(partes?|repuestos?|accesorios?|piezas?)\b/,
      resta: 4,
      soloPropia: true,
    },
    // Los desperdicios y la chatarra (8549, 7204) comparten palabras con todo.
    {
      en: /\b(desechos?|desperdicios?|chatarra|residuos?)\b/,
      pide: /\b(desechos?|desperdicios?|chatarra|residuos?)\b/,
      resta: 4,
    },
    // El monitor de tubo ya no se importa: un monitor hoy es LED o LCD.
    { en: /\brayos catodicos\b/, pide: /\b(catodic[a-z]*|crt)\b/, resta: 2 },
    // Un «panel LED» es una luminaria; los paneles fotovoltaicos, sólo si se pide algo solar.
    { en: /\bfotovoltaic/, pide: /\b(solar(es)?|fotovoltaic[a-z]*)\b/, resta: 3, soloPropia: true },
  ];

/**
 * El material dice de qué está hecho, no qué es: en «hervidor de acero inoxidable»
 * manda el hervidor, no los tubos de acero. Vale un tercio, salvo que sea lo
 * primero que se escribió («vidrio templado»).
 */
const MATERIALES = new Set([
  'acero',
  'inoxidable',
  'aluminio',
  'vidrio',
  'plastico',
  'madera',
  'metal',
  'metalico',
  'hierro',
  'cobre',
  'cuero',
  'algodon',
  'poliester',
  'ceramica',
  'porcelana',
  'caucho',
  'goma',
  'bambu',
  'papel',
  'carton',
]);

/**
 * Ordena las partidas por coincidencia y reparte 100 entre las `cuantas` primeras.
 *
 * 28-oct (prueba de punta a punta en DEV): antes se quedaba con las tres primeras
 * del buscador y les ponía 64/26/10 por el puesto. Recomendaba «Partes» (8415.90)
 * para un aire split, un decodificador satelital para un monitor y puertas de
 * aluminio para un panel solar. Ahora puntúa todas las candidatas:
 *
 * - cada palabra de la consulta vale 3 si está en la descripción de la partida, 2
 *   en la de su padre y 1 más arriba (más medio punto por cada otro nivel), sin
 *   contar lo que viene después de «excepto» o «sin»;
 * - una palabra que está en todas las candidatas no distingue nada: vale según qué
 *   tan rara es entre ellas;
 * - la primera palabra (casi siempre la que dice qué es: «licuadora», «silla»)
 *   vale el doble, y lo que entendió la IA suma con un 30 %;
 * - «Partes», los desperdicios y los monitores de tubo bajan si no se pidieron;
 * - el orden del buscador sólo desempata.
 */
export function puntuar<T extends PartidaParaPuntuar>(
  consulta: string,
  partidas: T[],
  cuantas = 3,
  pistas: Pistas = {}
): Array<Puntuada<T>> {
  const terminos = terminosDe(consulta);
  const deLaIa = terminosDe(
    [pistas.concepto ?? '', ...(pistas.terminosLegales ?? [])].join(' ')
  ).filter((t) => !terminos.includes(t));
  const consultaNormal = normalizar(consulta);
  const niveles = partidas.map(nivelesDe);
  const n = partidas.length;

  // Qué tan rara es cada forma entre las candidatas (como el «idf» de un buscador).
  const rareza = (esta: (texto: string) => boolean) => {
    const conElla = niveles.filter((nv) => nv.some((t) => t && esta(t))).length;
    return Math.log((n + 1) / (conElla + 0.5));
  };
  const valorDe = (termino: string, nv: string[]) =>
    formasDe(termino).reduce((s, esta) => {
      const pts = puntosDeForma(esta, nv);
      return pts > 0 ? s + pts * Math.max(0.2, rareza(esta)) : s;
    }, 0);

  const crudas = partidas.map((p, i) => {
    const nv = niveles[i];
    const coincide: string[] = [];
    let puntaje = 0;
    terminos.forEach((t, j) => {
      const v = valorDe(t, nv);
      if (v > 0) {
        puntaje += v * (j === 0 ? 2 : MATERIALES.has(t) ? 1 / 3 : 1);
        coincide.push(t);
      }
    });
    const coincideIa: string[] = [];
    for (const t of deLaIa) {
      const v = valorDe(t, nv);
      if (v > 0) {
        puntaje += 0.3 * v;
        coincideIa.push(t);
      }
    }
    const todo = nv.join(' ');
    for (const r of SALVO_QUE_SE_PIDA) {
      if (r.en.test(r.soloPropia ? nv[0] : todo) && !r.pide.test(consultaNormal))
        puntaje -= r.resta;
    }
    puntaje -= i * 0.01;
    return { partida: p, puntaje, coincide, coincideIa };
  });

  const elegidas = [...crudas].sort((a, b) => b.puntaje - a.puntaje).slice(0, cuantas);
  // Reparto «afilado»: las diferencias de puntaje se notan en el porcentaje.
  const pesos = elegidas.map((c) => Math.exp(0.6 * c.puntaje));
  const suma = pesos.reduce((s, w) => s + w, 0) || 1;
  const porcentajes = repartirCien(pesos.map((w) => (w / suma) * 100));
  return elegidas.map((c, i) => ({
    partida: c.partida,
    coincidencia: porcentajes[i],
    coincide: c.coincide,
    coincideIa: c.coincideIa,
  }));
}

/**
 * Enteros que suman exactamente 100, por el método de los mayores restos: cada
 * uno se queda con su parte entera y los puntos que faltan van a los de mayor
 * resto (a igual resto, al de más arriba).
 *
 * Así una lista ordenada nunca queda desordenada por el redondeo. Antes se
 * ajustaba el primero: con 44,6 / 44,5 / 10,9 se redondeaba a 45 / 45 / 11 y,
 * para volver a 100, la recomendada bajaba a 44 debajo de una de 45 (silla de
 * oficina en DEV, 28-oct).
 *
 * Ninguna queda en 0 %: una alternativa que se ofrece para elegir tiene poca
 * coincidencia, no ninguna. El punto sale de la mayor (con un monitor, la
 * recomendada daba 100 % y las otras dos «0 % coincidencia»).
 */
export function repartirCien(valores: number[]): number[] {
  if (valores.length === 0) return [];
  const enteros = valores.map((v) => Math.floor(v));
  let faltan = 100 - enteros.reduce((s, n) => s + n, 0);
  const porResto = valores
    .map((v, i) => ({ i, resto: v - Math.floor(v) }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i);
  for (const { i } of porResto) {
    if (faltan <= 0) break;
    enteros[i] += 1;
    faltan -= 1;
  }
  for (let i = 0; i < enteros.length; i++) {
    if (enteros[i] > 0) continue;
    const mayor = enteros.indexOf(Math.max(...enteros));
    if (enteros[mayor] <= 1) break;
    enteros[mayor] -= 1;
    enteros[i] = 1;
  }
  return enteros;
}
